// ccaaPages.ts — F15b: single source of truth for the 15 "Calculadora IRPF 2025 [CCAA]"
// indexable pages (plan §4 F15b). One entry per common-regime CCAA; the committed HTML files
// under calculadoras/irpf-2025/<slug>/ are rendered from this data by ccaaPageTemplate.ts and
// locked to it by __tests__/catalogPages.test.ts (regeneration guard). The React page reads
// the entry via `pageRegion` to fix the H1 and preselect the region — 15 URLs, ONE bundle.
//
// ZERO imports on purpose: generateCcaaPages.ts runs this file directly under Node's native
// type stripping (`node src/calculadoras/irpf2025/generateCcaaPages.ts`), so everything in
// the template chain must be dependency-free, erasable-only TypeScript.
//
// `name` mirrors CALCULATOR_REGIONS (calc.ts) and `regionCode`/`ccaa` mirror
// LANDING_REGION_TO_CCAA (landingEstimator.ts) — both equalities are test-locked, so the
// catalog can never drift from the engine's region table.

/** O4 (2026-08-14): custom domain purchased and verified — swapped from the .web.app default. */
export const CALC_HOST = 'https://gestorpro-app.com';

export interface CcaaCalcPage {
    /** URL slug: /calculadoras/irpf-2025/<slug>/ */
    slug: string;
    /** Landing region code — key of LANDING_REGION_TO_CCAA (test-locked, non-foral). */
    regionCode: string;
    /** engine2025 CCAA code (test-locked to LANDING_REGION_TO_CCAA[regionCode]). */
    ccaa: string;
    /** Display name — must equal the CALCULATOR_REGIONS entry for regionCode (test-locked). */
    name: string;
    /** Grammatical form after "en …" for titles/H1 ("la Comunidad de Madrid", "Andalucía"). */
    inPhrase: string;
}

export const CCAA_CALC_PAGES: CcaaCalcPage[] = [
    { slug: 'andalucia', regionCode: 'andalucia', ccaa: 'AN', name: 'Andalucía', inPhrase: 'Andalucía' },
    { slug: 'aragon', regionCode: 'aragon', ccaa: 'AR', name: 'Aragón', inPhrase: 'Aragón' },
    { slug: 'asturias', regionCode: 'asturias', ccaa: 'AS', name: 'Asturias', inPhrase: 'Asturias' },
    { slug: 'islas-baleares', regionCode: 'baleares', ccaa: 'IB', name: 'Baleares (Illes Balears)', inPhrase: 'Baleares (Illes Balears)' },
    { slug: 'canarias', regionCode: 'canarias', ccaa: 'CN', name: 'Canarias', inPhrase: 'Canarias' },
    { slug: 'cantabria', regionCode: 'cantabria', ccaa: 'CB', name: 'Cantabria', inPhrase: 'Cantabria' },
    { slug: 'castilla-la-mancha', regionCode: 'castilla_la_mancha', ccaa: 'CM', name: 'Castilla-La Mancha', inPhrase: 'Castilla-La Mancha' },
    { slug: 'castilla-y-leon', regionCode: 'castilla_y_leon', ccaa: 'CL', name: 'Castilla y León', inPhrase: 'Castilla y León' },
    { slug: 'cataluna', regionCode: 'cataluna', ccaa: 'CT', name: 'Cataluña (Catalunya)', inPhrase: 'Cataluña' },
    { slug: 'extremadura', regionCode: 'extremadura', ccaa: 'EX', name: 'Extremadura', inPhrase: 'Extremadura' },
    { slug: 'galicia', regionCode: 'galicia', ccaa: 'GA', name: 'Galicia', inPhrase: 'Galicia' },
    { slug: 'madrid', regionCode: 'madrid', ccaa: 'MD', name: 'Comunidad de Madrid', inPhrase: 'la Comunidad de Madrid' },
    { slug: 'murcia', regionCode: 'murcia', ccaa: 'MC', name: 'Región de Murcia', inPhrase: 'la Región de Murcia' },
    { slug: 'la-rioja', regionCode: 'rioja', ccaa: 'RI', name: 'La Rioja', inPhrase: 'La Rioja' },
    { slug: 'comunidad-valenciana', regionCode: 'valencia', ccaa: 'VC', name: 'Comunidad Valenciana', inPhrase: 'la Comunidad Valenciana' },
];

export const ccaaPagePath = (page: CcaaCalcPage): string => `/calculadoras/irpf-2025/${page.slug}/`;

/** Canonical URL — ALWAYS with trailing slash (F15a convention). */
export const ccaaPageUrl = (page: CcaaCalcPage): string => `${CALC_HOST}${ccaaPagePath(page)}`;

export const ccaaPageTitle = (page: CcaaCalcPage): string =>
    `Calculadora IRPF 2025 en ${page.inPhrase}: cuota y tipo efectivo | GestorPro`;

export const ccaaPageH1 = (page: CcaaCalcPage): string => `Calculadora IRPF 2025 en ${page.inPhrase}`;

export const ccaaPageDescription = (page: CcaaCalcPage): string =>
    `Calculadora de IRPF 2025 en ${page.inPhrase}, gratuita y sin registro: estima tu cuota y tu ` +
    `tipo efectivo con la escala estatal y la escala autonómica oficiales de Renta 2025 (AEAT/BOE) ` +
    `y consulta deducciones autonómicas de ${page.name}. El cálculo se hace en tu navegador: ` +
    `tus datos no se envían a ningún servidor.`;

export const ccaaPageOgDescription = (page: CcaaCalcPage): string =>
    `Estima tu IRPF 2025 en ${page.inPhrase} con las escalas oficiales (AEAT/BOE). ` +
    `Gratis, sin registro y calculado en tu navegador.`;
